import BackNextNav, { BackNextNavProps } from '@/components/common/BackNextNav';
import { FlexRow } from '@/components/common/Flex';
import { useIdvContext } from '@/components/idv/IdvProvider';
import { useMutation, useQuery } from '@apollo/client';
import { FlexProps } from '@chakra-ui/react';
import {
    CurrentUserProfileDocument,
    UpdateUserProfileDocument,
} from '@common/graphql/generated';
import { UserInfo } from '@common/react/user';
import { typedKeys } from '@common/util/objects';
import { isBoolean, isEmpty, isNullish, isObject } from '@common/util/types';
import isEqual from 'lodash.isequal';
import React, {
    createContext,
    MutableRefObject,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useDrawerContext } from '../common/Drawer';
import { FlexCol } from '../common/Flex';
import Form, { FormProps } from '../common/Form';
import InputField, { InputFieldProps } from '../common/InputField';

export type IdvFormType<
    T extends Record<string, any> | undefined = Record<string, never>,
    K extends keyof UserInfo = keyof UserInfo,
> =
    T extends Record<string, any>
        ? T & Partial<Pick<UserInfo, K>>
        : Partial<Pick<UserInfo, K>>;

interface IdvFormProps<T extends Record<string, any>>
    extends Partial<Omit<FormProps, 'children'>> {
    children?: React.ReactNode;
    /**
     * @param userProfile Current user profile
     * @returns { [key: keyof T]: any }
     *
     * The generic type `T` is provided by the consumer for each form and form input.
     * Each `key` is mapped to the `name` property of the `<InputField />` component.
     */
    initValues: (userProfile?: Partial<UserInfo>) => T;
    /**
     * @param values Current form state of all input values
     * @param userProfile Current user profile
     * @returns Desired user profile changes
     *
     * This callback is called when the form is submitted after validation.
     * The return value becomes an argument for `updateUserProfile()`.
     */
    toMutationInput: (
        values: T,
        userProfile?: Partial<UserInfo>
    ) => Partial<UserInfo>;
    beforeSubmit?: (values: T) => Promise<T | false>;
    backNextNavProps: BackNextNavProps;
}

interface IdvFormContext<T extends Record<string, any>> {
    values: T;
    updateValues: (change: Partial<T>) => void;
    requiredFieldsRef: MutableRefObject<{ [key: string]: boolean } | null>;
    setRequiredFields: (fields: { [key: string]: boolean }) => void;
    isButtonDisabled: boolean;
}

const IdvFormContext = createContext<IdvFormContext<any>>({
    values: {},
    updateValues: () => false,
    requiredFieldsRef: { current: null },
    setRequiredFields: () => false,
    isButtonDisabled: false,
});

export const IdvForm = <T extends Record<string, any>>({
    children,
    initValues,
    toMutationInput,
    beforeSubmit,
    backNextNavProps,
    ...rest
}: IdvFormProps<T>) => {
    const { onNext, onBack } = useIdvContext();
    const { isOpen: isDrawer, onClose: onDrawerClose } = useDrawerContext();
    const [updateUserProfile] = useMutation(UpdateUserProfileDocument);
    const { data } = useQuery(CurrentUserProfileDocument);
    const userProfile = (data?.currentUserProfile ||
        undefined) as Partial<UserInfo>;
    const [values, setValues] = useState(initValues(userProfile));
    const [requiredFields, setRequiredFields] = useState<{
        [key: string]: boolean;
    }>({});
    /**
     * This ref ensures that requiredFields can be properly overwritten when multiple inputs are unmounted at the same time.
     */
    const requiredFieldsRef = useRef(requiredFields);
    const [isLoading, setIsLoading] = useState(false);
    const isDisabled = !typedKeys(values)
        .filter((key) => !!requiredFields?.[key as string])
        .every((key) => !isEmpty(values[key]));

    // Keep this logic in sync with mobile/components/IdvLayout.tsx
    const onSubmit = useCallback(async () => {
        if (isLoading || isDisabled) return;
        setIsLoading(true);

        let valuesToSubmit = values;
        if (beforeSubmit) {
            const res = await beforeSubmit(values);
            if (!res) {
                setIsLoading(false);
                return;
            }
            valuesToSubmit = res;
        }

        const userProfileChanges = toMutationInput(valuesToSubmit, userProfile);
        const changes = typedKeys(userProfileChanges).reduce((acc, key) => {
            const newValue = userProfileChanges[key];
            const existingValue = userProfile[key];
            const value = isObject(newValue)
                ? {
                      ...(existingValue as object),
                      ...(newValue as object),
                  }
                : newValue;
            return {
                ...acc,
                [key]: value,
            };
        }, {});
        const response = await updateUserProfile({
            variables: { patch: changes },
        });

        /**
         * On the Summary page we allow users to edit fields by opening a drawer.
         * We do not want to update the IDV position for this use case.
         */
        if (isDrawer) {
            onDrawerClose();
        } else {
            onNext(
                response?.data?.updateCurrentUserProfile?.userProfile
                    ?.onboardingData || undefined
            );
        }
        setIsLoading(false);
    }, [
        values,
        updateUserProfile,
        toMutationInput,
        beforeSubmit,
        isLoading,
        setIsLoading,
        onNext,
        userProfile,
        isDisabled,
        isDrawer,
        onDrawerClose,
    ]);

    const updateValues = useCallback(
        (change: Partial<T>) => {
            setValues({ ...values, ...change });
        },
        [values]
    ) as any;

    /**
     * Update the form values if the user profile is updated outside of the form.
     */
    useEffect(() => {
        const updatedProfileValues = initValues(userProfile);
        if (isEqual(updatedProfileValues, values)) {
            updateValues(updatedProfileValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProfile]);

    const { isDisabled: backNextDisabled, ...backNextNavPropsRest } = {
        ...backNextNavProps,
        ...(isDrawer
            ? {
                  backButtonText: 'Cancel',
                  nextButtonText: 'Save changes',
                  onBack: onDrawerClose,
              }
            : {}),
    };

    return (
        <IdvFormContext.Provider
            value={{
                values,
                updateValues,
                requiredFieldsRef,
                setRequiredFields,
                isButtonDisabled: isDisabled,
            }}
        >
            <Form
                flex={1}
                flexDir="column"
                disabled={isLoading}
                onSubmit={onSubmit}
                fadeOnDisabled={false}
                {...rest}
            >
                <FlexCol basis={0} grow={1} overflow="auto">
                    {children}
                </FlexCol>
                <BackNextNav
                    grow={0}
                    mt={10}
                    onBack={onBack}
                    isDisabled={
                        isBoolean(backNextDisabled)
                            ? backNextDisabled
                            : isDisabled || isLoading
                    }
                    shouldFormSubmit
                    {...backNextNavPropsRest}
                />
            </Form>
        </IdvFormContext.Provider>
    );
};

export const useIdvFormContext = <T extends Record<string, any>>() =>
    useContext<IdvFormContext<T>>(IdvFormContext);

const Row = ({ children, ...props }: FlexProps) => (
    <FlexRow
        gap={{ base: 4, lg: 8, '2xl': 12 }}
        mb={{ base: 8, '2xl': 10 }}
        align="flex-end"
        {...props}
    >
        {children}
    </FlexRow>
);

type InputProps<T extends Record<string, any>> = {
    name: keyof T;
    onChange?: (value: any, callback?: (changes: Partial<T>) => void) => void;
    autoFocus?: boolean;
} & Omit<InputFieldProps, 'name' | 'type' | 'onChange' | 'as'> &
    Partial<Pick<InputFieldProps, 'type'>>;

const Input = <T extends Record<string, any>>({
    value,
    name,
    type = 'text',
    onChange = () => false,
    autoFocus,
    inputOptions,
    isRequired,
    ...props
}: InputProps<T>) => {
    const { values, updateValues, requiredFieldsRef, setRequiredFields } =
        useIdvFormContext();

    const updateRequiredFields = useCallback(
        (override?: boolean) => {
            const fields = {
                ...requiredFieldsRef.current,
                [name]: override ?? !!isRequired,
            };
            if (!isEqual(fields, requiredFieldsRef.current)) {
                requiredFieldsRef.current = fields;
                setRequiredFields(fields);
            }
        },
        [requiredFieldsRef, setRequiredFields, isRequired, name]
    );

    useEffect(() => updateRequiredFields(), [updateRequiredFields]);

    /**
     * If this input is unmounted, cleanup the context providers requiredFields value
     */
    useEffect(() => {
        return () => updateRequiredFields(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (values[name as string] !== value && !isNullish(value)) {
            updateValues({ [name]: value });
        }
    }, [values, updateValues, value, name]);

    return (
        <InputField
            value={values[name as string] ?? ''}
            name={name as string}
            type={type}
            /**
             * Ordinarily, we automatically update the form state according to each inputs' respective input name.
             * A callback for `onChange` is provided in the event the consumer wants to update additional state properties.
             *
             * Example:
                <IdvForm.Input
                    onChange={(value, updateValues) =>
                        updateValues({
                            otherProperty: value === 'foo' ? 'bar' : undefined,
                        })
                    }
                />;
             */
            onChange={(value: any) => {
                updateValues({ [name]: value });
                onChange?.(value, (changes: Partial<T>) =>
                    updateValues({ [name]: value, ...changes })
                );
            }}
            autoFocus={autoFocus}
            inputProps={{ autoFocus }}
            inputOptions={inputOptions}
            {...props}
        />
    );
};

IdvForm.Row = Row;
IdvForm.Input = Input;
